import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { key: 'projects', to: '/#projects' },
    { key: 'teams', to: '/teams' },
    { key: 'contact', to: '/#contact' },
  ];

  return (
    <header
      className={clsx(
        'fixed top-0 left-0 w-full z-50 transition-all duration-300 ease-in-out',
        isScrolled || !isHome ? 'bg-white/95 backdrop-blur-sm py-4 shadow-sm' : 'bg-transparent py-6'
      )}
    >
      <div className="container mx-auto px-4 md:px-8 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="z-50">
          <img 
            src="/assets/images/logo.png" 
            alt="Logo" 
            className={clsx("transition-all duration-300", isScrolled || !isHome ? "h-8" : "h-10")} 
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => {
            const isHash = link.to.includes('#');
            return isHash ? (
              <a
                key={link.key}
                href={link.to}
                onClick={(e) => {
                  e.preventDefault();
                  const id = link.to.split('#')[1];
                  if (isHome) {
                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    navigate('/');
                    setTimeout(() => {
                      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }
                }}
                className={clsx(
                  "text-sm font-medium tracking-wide transition-colors hover:opacity-60",
                  (isScrolled || !isHome) ? "text-studio-black" : "text-studio-black"
                )}
              >
                {t(`nav.${link.key}`)}
              </a>
            ) : (
              <Link
                key={link.key}
                to={link.to}
                className={clsx(
                  "text-sm font-medium tracking-wide transition-colors hover:opacity-60",
                  (isScrolled || !isHome) ? "text-studio-black" : "text-studio-black"
                )}
              >
                {t(`nav.${link.key}`)}
              </Link>
            );
          })}
        </nav>

        {/* Language Switcher (Desktop) */}
        <div className="hidden md:flex items-center space-x-4 text-sm font-medium">
          {['fr', 'en', 'zh'].map((lng) => (
            <button
              key={lng}
              onClick={() => changeLanguage(lng)}
              className={clsx(
                "uppercase transition-opacity hover:opacity-100",
                i18n.language === lng ? "opacity-100 border-b border-black" : "opacity-40",
                (isScrolled || !isHome) ? "text-black" : "text-black"
              )}
            >
              {lng === 'zh' ? '中文' : lng}
            </button>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden z-50 p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 bg-white z-40 flex flex-col items-center justify-center space-y-8">
            {navLinks.map((link) => {
              const isHash = link.to.includes('#');
              return isHash ? (
                <a
                  key={link.key}
                  href={link.to}
                  className="text-2xl font-light"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    const id = link.to.split('#')[1];
                    if (isHome) {
                      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      navigate('/');
                      setTimeout(() => {
                        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }
                  }}
                >
                  {t(`nav.${link.key}`)}
                </a>
              ) : (
                <Link
                  key={link.key}
                  to={link.to}
                  className="text-2xl font-light"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(`nav.${link.key}`)}
                </Link>
              );
            })}
            <div className="flex space-x-6 mt-8">
              {['fr', 'en', 'zh'].map((lng) => (
                <button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  className={clsx(
                    "text-lg uppercase",
                    i18n.language === lng ? "font-bold border-b border-black" : "opacity-50"
                  )}
                >
                  {lng === 'zh' ? '中文' : lng}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
